/* eslint-disable import/no-anonymous-default-export */
import type { AxiosResponse } from "axios";
import apiClient from "@utilities/apiClient";
import EndpointsConst from "@consts/EndpointsConst";

interface AddUserPayload {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  position: string | null;
  password: string | null;
  confirmPassword: string | null;
}

type EditUserPayload = Pick<User, "email" | "firstName" | "lastName"> & {
  position: string;
};

interface LoginPayload {
  email: string;
  password: string;
}

class UserService {
  /**
   * [POST]
   * Adds user to database
   * @param userData - The new user's email, name, position, and password
   */
  addUser(userData: AddUserPayload): Promise<AxiosResponse> {
    return apiClient.post(EndpointsConst.API.USERS.ADD_USER, userData);
  }

  /**
   * [POST]
   * Changes Password for User
   * @param email - The account's email
   * @param currentPassword - The account's current password
   * @param updatedPassword - The new password to set
   */
  changePassword(
    email: string,
    currentPassword: string,
    updatedPassword: string,
  ): Promise<AxiosResponse> {
    return apiClient.post(EndpointsConst.API.USERS.CHANGE_PASSWORD, {
      email,
      currentPassword,
      updatedPassword,
    });
  }

  /**
   * [DELETE]
   * Deletes user by email
   * @param email - The email of the user to delete
   */
  deleteUser(email: string): Promise<AxiosResponse> {
    return apiClient.delete(EndpointsConst.API.USERS.DELETE_USER, {
      params: { email },
    });
  }

  /**
   * [POST]
   * Edits a user's profile
   * @param formValues - The user's email, name, and position
   */
  editUser(formValues: EditUserPayload): Promise<AxiosResponse> {
    return apiClient.post(EndpointsConst.API.USERS.EDIT_USER, formValues);
  }

  /**
   * [POST]
   * Generates and emails the user a temporary password
   * @param email - The account's email
   */
  generateTemporaryPassword(email: string): Promise<AxiosResponse> {
    return apiClient.post(
      EndpointsConst.API.USERS.GENERATE_TEMPORARY_PASSWORD,
      { email },
    );
  }

  /**
   * [GET]
   * Retrieves all users
   */
  getAllUsers(): Promise<AxiosResponse<User[]>> {
    return apiClient.get(EndpointsConst.API.USERS.GET_ALL_USERS);
  }

  /**
   * [GET]
   * Retrieves total user count
   */
  getTotalUserCount(): Promise<AxiosResponse<number>> {
    return apiClient.get(EndpointsConst.API.USERS.GET_TOTAL_USER_COUNT);
  }

  /**
   * [GET]
   * Retrieves user by email
   * @param email - The email of the user to retrieve
   */
  getUserByEmail(email: string): Promise<AxiosResponse> {
    return apiClient.get(EndpointsConst.API.USERS.GET_USER_BY_EMAIL, {
      params: { email },
    });
  }

  /**
   * [POST]
   * Attempts to log the user in, responding based on account status
   * @param formValues - The account's email and password
   */
  login(formValues: LoginPayload): Promise<AxiosResponse> {
    return apiClient.post(EndpointsConst.API.USERS.LOGIN, formValues);
  }

  /**
   * [GET]
   * Retrieves the currently signed-in user, or rejects with 401 if there's no active session
   */
  getCurrentUser(): Promise<AxiosResponse<User>> {
    return apiClient.get(EndpointsConst.API.USERS.ME);
  }

  /**
   * [POST]
   * Ends the current session
   */
  logout(): Promise<AxiosResponse> {
    return apiClient.post(EndpointsConst.API.USERS.LOGOUT);
  }

  /**
   * [POST]
   * Resets Password for User, proving identity with the emailed temporary password
   * @param email - The account's email
   * @param tempPassword - The temporary password emailed to the account
   * @param password - The new password to set
   */
  resetPassword(
    email: string,
    tempPassword: string,
    password: string,
  ): Promise<AxiosResponse> {
    return apiClient.post(EndpointsConst.API.USERS.RESET_PASSWORD, {
      email,
      tempPassword,
      password,
    });
  }

  /**
   * [POST]
   * Updates the user's account settings, uploading the profile picture to AWS when provided
   * @param formData - Form data containing the account fields and optional profile picture file
   */
  updateAccount(formData: FormData): Promise<AxiosResponse> {
    return apiClient.post(EndpointsConst.API.USERS.UPDATE_ACCOUNT, formData);
  }
}

export default new UserService();
